import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';
import { type StaticPage } from './staticContents';

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    statusCode?: number;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

type AdminStaticPageListResponse = {
    page: number;
    pageSize: number;
    totalCount: number;
    items: StaticPage[];
};

type AdminStaticPageMutationInput = {
    title: string;
    slug?: string;
    content: string;
};

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function trimSlashes(value: string): string {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    return { baseUrl, apiVersion, adminToken };
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct =
            candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }
    return fallback;
}

function unwrapAdminApiPayload<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) {
        return payload as T;
    }

    const envelope = payload as AdminApiEnvelope<T>;

    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Admin static page request failed'));
    }

    if (envelope.data !== undefined) {
        return envelope.data;
    }

    return payload as T;
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : {};
}

function asString(value: unknown, fallback = ''): string {
    return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function normalizeStaticPage(value: unknown): StaticPage {
    const record = asRecord(value);
    const staticPageId = asString(record.staticPageId);

    return {
        id: staticPageId,
        staticPageId,
        title: asString(record.title),
        slug: asString(record.slug),
        content: asString(record.content),
        createdAt: asString(record.createdAt),
        updatedAt: asString(record.updatedAt),
    };
}

function normalizeStaticPageListResponse(value: unknown): AdminStaticPageListResponse {
    const payload = unwrapAdminApiPayload(value as AdminStaticPageListResponse | AdminApiEnvelope<AdminStaticPageListResponse>);
    const record = asRecord(payload);
    const items = Array.isArray(record.items) ? record.items.map(normalizeStaticPage) : [];

    return {
        page: asNumber(record.page, 1),
        pageSize: asNumber(record.pageSize, items.length || 1),
        totalCount: asNumber(record.totalCount, items.length),
        items,
    };
}

async function requestAdminStaticPages<T>(
    path: string,
    init: RequestInit = {},
    query?: Record<string, string>,
): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const resourcePath = trimSlashes(path);
    const url = new URL(resourcePath ? `${apiRoot}/AdminStaticPages/${resourcePath}` : `${apiRoot}/AdminStaticPages`);

    for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== '') {
            url.searchParams.set(key, value);
        }
    }

    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }
    if (init.body && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (adminToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), {
        ...init,
        headers,
    });

    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
        const envelope = payload as AdminApiEnvelope<unknown>;
        if (envelope.success === false) {
            throw new Error(getErrorMessage(envelope, 'Admin static page request failed'));
        }
    }

    return payload as T;
}

function toMutationBody(input: AdminStaticPageMutationInput): AdminStaticPageMutationInput {
    const title = input.title.trim();
    const content = input.content.trim();
    const slug = input.slug?.trim();

    return {
        title,
        content,
        ...(slug ? { slug } : {}),
    };
}

export async function getAdminStaticPageList(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
}): Promise<AdminStaticPageListResponse> {
    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 100;
    const search = params?.search?.trim() ?? '';
    const response = await requestAdminStaticPages<unknown>('', { method: 'GET' }, {
        page: String(page),
        pageSize: String(pageSize),
        search,
    });

    return normalizeStaticPageListResponse(response);
}

export async function createAdminStaticPage(input: AdminStaticPageMutationInput): Promise<StaticPage> {
    const response = await requestAdminStaticPages<unknown>('', {
        method: 'POST',
        body: JSON.stringify(toMutationBody(input)),
    });

    return normalizeStaticPage(unwrapAdminApiPayload(response as StaticPage | AdminApiEnvelope<StaticPage>));
}

export async function updateAdminStaticPage(
    staticPageId: string,
    input: AdminStaticPageMutationInput,
): Promise<StaticPage> {
    const response = await requestAdminStaticPages<unknown>(staticPageId, {
        method: 'PUT',
        body: JSON.stringify(toMutationBody(input)),
    });

    return normalizeStaticPage(unwrapAdminApiPayload(response as StaticPage | AdminApiEnvelope<StaticPage>));
}

export async function deleteAdminStaticPage(staticPageId: string): Promise<void> {
    await requestAdminStaticPages(staticPageId, {
        method: 'DELETE',
    });
}